import { blue, grey } from "@faro-lotv/flat-ui";
import { Box } from "@mui/system";
import { type AnchorHTMLAttributes, type JSX } from "react";
import Markdown from "react-markdown";
import { BoundingBoxLink } from "./bounding-box-link";
import { ElementLink } from "./element-link";

type AiMarkdownProps = {
  children: string;

  // Whether the markdown is being rendered as part of the agent's reasoning trace.
  // If true, applies different styling to visually distinguish it from markdown rendered in other contexts.
  isReasoning?: boolean;
};

/**
 * @returns a markdown string with support for custom link types.
 * Custom link prefixes (`element#` and `bounding-box#`) are handled by {@link MarkdownLink}.
 * All other markdown is rendered using standard react-markdown output.
 */
export function AiMarkdown({
  children,
  isReasoning,
}: AiMarkdownProps): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        ...(isReasoning && {
          color: grey[600],
          fontSize: "0.8em",
          "& .FaroText-hyperLink": { color: blue[400] },
        }),
      }}
    >
      <Markdown
        components={{
          // Using custom component for markdown links instead of standard,
          // allowing us to use custom logic for those custom link tags
          a: MarkdownLink,
        }}
      >
        {children}
      </Markdown>
    </Box>
  );
}

/**
 * Custom component for all markdown links (i.e., text with the format `[link text](URL)`).
 * It handles two kinds of custom link prefixes:
 * - `element#`: assumed to reference a scene element by ID. A clickable text is rendered that, when clicked,
 *     dispatches store actions to set the active element and switch to walk mode.
 * - `bounding-box#`: assumed to reference a bounding box by ID. A clickable text is rendered that, when clicked,
 *     dispatches store actions to set the active element and switch to walk mode.
 *
 * For all other URLs, a regular anchor tag is rendered that opens the link in a new tab.
 *
 * @returns either a real link, a clickable element reference, a bounding box label, or a new-tab anchor
 */
function MarkdownLink({
  href,
  children,
}: AnchorHTMLAttributes<HTMLAnchorElement>): JSX.Element {
  // Splitting on `#` separates the scheme from the ID for both custom link types:
  // - `element#<guid>`      → scheme = "element",      id = "<guid>"
  // - `bounding-box#<guid>` → scheme = "bounding-box", id = "<guid>"
  // Regular URLs (e.g. `https://example.com/docs#section`) also contain `#`, but their
  // scheme will be something like `https://example.com/docs`, which never matches a
  // custom prefix, so they safely fall through to the default `<a>` rendering below.
  const [scheme, id] = href?.split("#") ?? [];

  // Check if the link matches the `element#` scheme
  if (scheme === "element" && id) {
    return <ElementLink elementId={id}>{children}</ElementLink>;
  }

  // Check if the link matches the `bounding-box#` scheme
  if (scheme === "bounding-box" && id) {
    return <BoundingBoxLink boxId={id}>{children}</BoundingBoxLink>;
  }

  // Default rendering for regular links, opening it in a new tab
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}
